import React, { useState } from 'react';
import { Document, Page } from 'react-pdf';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import '../../../css/preview.css';

const PdfPreview = ({
  pdfUrl,
  fileName,
  onDocumentLoadSuccess,
  onDocumentLoadError,
  pageNumber,
  numPages,
  goToPrevPage,
  goToNextPage,
  colorPages,
  setSwiperInstance,
  onDownload,
  isDark,
  pdfError,
}) => {

  return (
    <div className={`border-solid border-[1px] box-border content-stretch flex flex-col gap-[12px] items-start p-[20px] relative rounded-[12px] w-full transition-colors duration-200 ${
      isDark ? 'bg-[#000000] border-[#292929]' : 'bg-white border-[#e9e9e7]'
    }`}>
      {/* Title */}
      <p className={`font-bold leading-[normal] relative shrink-0 text-[16px] m-0 transition-colors duration-200 ${
        isDark ? 'text-white' : 'text-[#072837]'
      }`}>
        Attached Documents
      </p>

      {/* Main Content */}
      <div className={`border-solid border-[1px] box-border content-stretch flex flex-col gap-[16px] items-start p-[20px] relative rounded-[10px] shrink-0 w-full transition-colors duration-200 ${
        isDark ? 'bg-[#161515] border-[#292929]' : 'bg-[#fff5eb] border-[#e9e9e7]'
      }`}>
        {/* Document Header */}
        <div className="content-stretch flex flex-col gap-2 items-start relative shrink-0 w-full">
          <div className="flex gap-[16px] items-center relative w-full">
            <p className={`basis-0 font-normal grow leading-[normal] min-h-px min-w-px relative text-[16px] m-0 transition-colors duration-200 break-words ${
              isDark ? 'text-[#b0b0b0]' : 'text-[#686868]'
            }`}>
              {fileName}
            </p>
            <div 
              className="relative shrink-0 size-[16px] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                window.open(pdfUrl, '_blank', 'noopener,noreferrer');
              }}
              title="Open in new tab"
            >
              <div
                className="w-[16px] h-[16px]"
                style={{
                  backgroundColor: isDark ? '#b0b0b0' : '#cd6d2c',
                  WebkitMaskImage: "url('/assets/icons/expand.svg')",
                  WebkitMaskRepeat: 'no-repeat',
                  WebkitMaskSize: 'contain',
                  WebkitMaskPosition: 'center',
                  maskImage: "url('/assets/icons/expand.svg')",
                  maskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  maskPosition: 'center',
                  transition: 'background-color 0.2s ease'
                }}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>

        {/* PDF Preview Section */}
        <Swiper
          navigation={false}
          pagination={false}
          modules={[Navigation, Pagination]}
          className="w-full"
          onSwiper={setSwiperInstance}
          allowTouchMove={false}
        >
          <SwiperSlide>
            <div className="content-stretch flex flex-col gap-[16px] items-center relative shrink-0 w-full">
              {/* PDF Container */}
              <div className={`border-solid border-[1px] min-h-[600px] relative rounded-[8px] shrink-0 w-full overflow-auto flex items-center justify-center p-4 transition-colors duration-200 ${
                isDark ? 'bg-[#000000] border-[#292929]' : 'bg-white border-[#cbcbcb]'
              }`}>
                {pdfError ? (
                  <div className="text-center p-4 transition-colors duration-200 text-[#ff4d4f]">
                    Failed to load PDF file
                  </div>
                ) : (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={
                      <div className={`text-center p-4 transition-colors duration-200 ${
                        isDark ? 'text-white' : 'text-[#333333]'
                      }`}>
                        Loading preview...
                      </div>
                    }
                  >
                    <Page
                      pageNumber={pageNumber}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={1.5}
                      className="pdf-page"
                    />
                  </Document>
                )}
              </div>

              {/* Navigation Controls - Only show if no PDF error */}
              {!pdfError && (
                <div className="content-stretch flex flex-col gap-[2px] items-start relative shrink-0 w-full">
                  <div className="content-stretch flex gap-[16px] items-start relative shrink-0 w-full">
                    {/* Left Chevron */}
                    <div className="content-stretch flex flex-col gap-[8px] items-center justify-center relative shrink-0">
                      <button
                        onClick={goToPrevPage}
                        disabled={pageNumber <= 1}
                        className={`flex items-center justify-center bg-transparent border-0 cursor-pointer p-0 ${pageNumber <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <ChevronLeft className={`w-[18px] h-[18px] transition-colors duration-200 ${
                          isDark ? 'text-white' : 'text-[#072837]'
                        }`} />
                      </button>
                    </div>

                    {/* Center - Page Info */}
                    <div className="basis-0 box-border content-stretch flex flex-col grow items-center justify-center min-h-px min-w-px relative shrink-0">
                      <p className={`font-semibold leading-[normal] relative shrink-0 text-[14px] text-center text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                        isDark ? 'text-[#b0b0b0]' : 'text-[#898989]'
                      }`}>
                        {pageNumber} of {numPages}
                      </p>
                      {colorPages?.includes(pageNumber.toString()) && (
                        <div className="box-border content-stretch flex flex-col gap-[10px] items-start justify-center px-[6px] py-[2px] relative rounded-[30px] shrink-0">
                          <div className="content-stretch flex gap-[2px] items-center relative shrink-0">
                            <p className={`font-normal italic leading-[normal] relative shrink-0 text-[10px] text-center text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                              isDark ? 'text-white' : 'text-black'
                            }`}>
                              (Marked as Color)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Chevron */}
                    <div className="content-stretch flex flex-col gap-[8px] items-center justify-center relative shrink-0">
                      <button
                        onClick={goToNextPage}
                        disabled={pageNumber >= numPages}
                        className={`flex items-center justify-center bg-transparent border-0 cursor-pointer p-0 ${pageNumber >= numPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <ChevronRight className={`w-[18px] h-[18px] transition-colors duration-200 ${
                          isDark ? 'text-white' : 'text-[#072837]'
                        }`} />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </SwiperSlide>
        </Swiper>

        {/* Download Button - Only show if no PDF error */}
        {!pdfError && (
          <div className="content-stretch flex flex-col items-center justify-center relative shrink-0 w-full">
            <div className="content-stretch flex gap-[16px] items-center justify-center relative shrink-0 w-full">
              <button
                onClick={onDownload}
                className={`basis-0 box-border content-stretch flex flex-col gap-[10px] grow items-center justify-center min-h-px min-w-px px-[16px] py-[12px] relative rounded-[12px] shrink-0 border-0 cursor-pointer transition-colors duration-200 ${
                  isDark
                    ? 'bg-[#cd6d2c] hover:bg-[#ffffff]'
                    : 'bg-[#072837] hover:bg-[#cd6d2c]'
                }`}
              >
                <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
                  <div
                    className="w-[16px] h-[16px]"
                    style={{
                      backgroundColor: isDark ? '#000000' : '#ffffff',
                      WebkitMaskImage: "url('/assets/icons/download.svg')",
                      WebkitMaskRepeat: 'no-repeat',
                      WebkitMaskSize: 'contain',
                      WebkitMaskPosition: 'center',
                      maskImage: "url('/assets/icons/download.svg')",
                      maskRepeat: 'no-repeat',
                      maskSize: 'contain',
                      maskPosition: 'center',
                      transition: 'background-color 0.2s ease'
                    }}
                    aria-hidden="true"
                  />
                  <p className={`font-semibold leading-[normal] relative shrink-0 text-[16px] text-nowrap whitespace-pre m-0 transition-colors duration-200 ${
                    isDark ? 'text-[#000000]' : 'text-white'
                  }`}>
                    Download Full Document
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPreview;


